import React from 'react';
import { Button, CollapsibleSection, Tab, TabList, TabPanel, Tabs, Text, useToast } from '@chatsdet/ui';
import { CheckCircle, Copy, MinusCircle, Play, XCircle } from '@phosphor-icons/react';
import { elapsedTestAttemptMs, type ExternalTestBddArgument } from '@chatsdet/types';
import { formatDurationMs, formatElapsedDurationMs } from '../../../lib/formatDuration';
import { ExternalTestOutputSection } from './ExternalTestOutputSection';
import { CopyButton } from '../../components/CopyButton';
import type {
  Scenario,
  ScenarioStep,
  StepVerdict,
} from '../../../features/external-tests/scenarios';
import type {
  ExternalRunDetailDto,
  ExternalRunItemDto,
} from '../../../features/external-tests/useExternalRun';
import type { ExternalTestStep } from '@chatsdet/types';
import { groupNativeSteps, parseExpectedActual, stripAnsi, type StepFilter } from './stepModel';

/**
 * Step status in the same vocabulary the native run step list uses, so a step
 * here reads exactly like a step there — same dot, same tone, same label.
 */
const STATUS: Record<StepVerdict, { label: string; tone: string; dot: string }> = {
  passed: { label: 'Success', tone: 'text-success', dot: 'bg-success' },
  failed: { label: 'Failed', tone: 'text-destructive', dot: 'bg-destructive' },
  flaky: { label: 'Warning', tone: 'text-amber-600 dark:text-amber-400', dot: 'bg-amber-500' },
  skipped: { label: 'Skipped', tone: 'text-muted-foreground', dot: 'bg-muted' },
};

function StepRow({ step, onSeek }: { step: ScenarioStep; onSeek?: (ms: number) => void }) {
  const status = STATUS[step.verdict];
  const offsetMs = step.capture?.videoOffsetMs ?? null;
  const seekable = offsetMs !== null && onSeek;

  // `px-3` lives HERE, not on the scroll container, so the hover fill spans the
  // full rail and the gutter is inside the hit area. With the padding outside,
  // the highlight floated in a 12px moat and the row's own edge was dead space.
  const body = (
    <div className="w-full px-3 py-1.5">
      <div className="flex w-full items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className={`h-2 w-2 shrink-0 rounded-full ${status.dot}`} aria-hidden="true" />
          <span className="min-w-0 whitespace-pre-wrap break-words text-sm">{step.stepLabel}</span>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {step.verdict !== 'passed' ? (
            <Text size="xs" className={status.tone}>
              {status.label}
            </Text>
          ) : null}
          <Text size="xs" className="w-12 text-right text-muted-foreground">
            {step.durationMs != null ? formatDurationMs(step.durationMs) : '—'}
          </Text>
        </div>
      </div>
      {step.error?.message && (
        <Text
          size="xs"
          className="ml-4 mt-0.5 line-clamp-2 text-destructive"
          title={step.error.message.split('\n')[0]}
        >
          {step.error.message.split('\n')[0]}
        </Text>
      )}
    </div>
  );

  // Only steps inside the recording are clickable — a step with no captured
  // window has nowhere to seek to, and a dead-looking button is worse than a
  // plain row.
  return seekable ? (
    <button
      type="button"
      onClick={() => onSeek(offsetMs)}
      className="flex w-full text-left transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
    >
      {body}
    </button>
  ) : (
    body
  );
}

function BddArgumentView({ argument }: { argument: ExternalTestBddArgument }) {
  if (argument.kind === 'docString') {
    return (
      <div className="mt-1">
        {argument.mediaType ? (
          <Text size="xs" className="mb-1 text-muted-foreground">
            {argument.mediaType}
          </Text>
        ) : null}
        <div className="relative">
          <CopyButton
            value={argument.content}
            label="BDD doc string"
            variant="surface"
            className="!opacity-100"
          />
          <pre
            className="overflow-x-auto whitespace-pre-wrap break-words border-l-2 border-border pl-2 pr-8 font-mono text-xs text-foreground"
            data-testid="bdd-doc-string"
          >
            {argument.content}
          </pre>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-1 overflow-x-auto" data-testid="bdd-data-table">
      <div className="relative">
        <CopyButton
          value={argument.rows.map((row) => row.join('\t')).join('\n')}
          label="BDD data table"
          variant="surface"
          className="!opacity-100"
        />
        <table className="border-collapse text-xs">
          <tbody>
            {argument.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className="border border-border px-2 py-1 align-top whitespace-pre-wrap"
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const STEP_ICON: Record<ExternalTestStep['status'], React.ReactNode> = {
  passed: <CheckCircle size={16} weight="fill" className="text-success" aria-label="Passed" />,
  failed: <XCircle size={16} weight="fill" className="text-destructive" aria-label="Failed" />,
  skipped: (
    <MinusCircle size={16} weight="fill" className="text-muted-foreground" aria-label="Skipped" />
  ),
};

type StepFailure = { message: string; stack?: string };

/** The failing step's error, expanded inline under the step. */
function FailureBlock({
  failure,
  onWatch,
}: {
  failure: StepFailure;
  onWatch?: () => void;
}) {
  const { toast } = useToast();
  const message = stripAnsi(failure.message);
  const stack = failure.stack ? stripAnsi(failure.stack) : null;
  const expectedActual = parseExpectedActual(failure.message);

  const copyError = async () => {
    try {
      await navigator.clipboard.writeText(stack && !stack.includes(message) ? `${message}\n${stack}` : (stack ?? message));
      toast.success('Copied error');
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <div className="space-y-2 pb-3 pl-[3.25rem] pr-4" data-testid="step-failure">
      <pre
        className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md border border-destructive/30 bg-destructive/5 p-3 font-mono text-xs text-foreground"
        data-testid="step-error"
      >
        {message}
      </pre>
      {expectedActual ? (
        <div className="grid grid-cols-2 gap-2" data-testid="step-expected-actual">
          {(
            [
              ['Expected', expectedActual.expected],
              ['Actual', expectedActual.actual],
            ] as const
          ).map(([label, value]) => (
            <div key={label} className="min-w-0 rounded-md border border-border bg-muted/30 p-2">
              <Text size="xs" className="text-muted-foreground">
                {label}
              </Text>
              <div className="break-words font-mono text-xs">{value}</div>
            </div>
          ))}
        </div>
      ) : null}
      {stack && stack !== message ? (
        <details>
          <summary className="cursor-pointer text-xs text-muted-foreground">Stack trace</summary>
          <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-xs text-muted-foreground">
            {stack}
          </pre>
        </details>
      ) : null}
      <div className="flex items-center gap-2">
        {onWatch ? (
          <Button variant="outline" size="sm" iconLeft={<Play size={14} aria-hidden="true" />} onPress={onWatch}>
            Watch this step
          </Button>
        ) : null}
        <Button variant="outline" size="sm" iconLeft={<Copy size={14} aria-hidden="true" />} onPress={copyError}>
          Copy error
        </Button>
      </div>
    </div>
  );
}

function NativeStepRow({
  step,
  number,
  failure,
  onWatch,
}: {
  step: ExternalTestStep;
  number: number;
  failure?: StepFailure;
  onWatch?: () => void;
}) {
  const keyword = step.bdd?.keyword.trim() ?? '';
  // Some providers already prefix the title with its keyword; don't say it twice.
  const showKeyword = keyword !== '' && !step.title.startsWith(keyword);
  return (
    <div
      className={`border-b border-border last:border-b-0 ${step.status === 'failed' ? 'bg-destructive/5' : ''}`}
      data-testid="native-step-row"
    >
      <div className="flex w-full items-start gap-3 px-4 py-2">
        <Text size="xs" className="w-5 shrink-0 pt-0.5 text-right tabular-nums text-muted-foreground">
          {number}
        </Text>
        <span className="shrink-0 pt-0.5">{STEP_ICON[step.status]}</span>
        <div className="min-w-0 flex-1">
          <span className="whitespace-pre-wrap break-words text-sm">
            {showKeyword ? (
              <span className="mr-1.5 font-medium text-muted-foreground">{keyword}</span>
            ) : null}
            {step.title}
          </span>
          {step.bdd ? (
            <div className="mt-0.5 space-y-1">
              <Text size="xs" className="font-mono text-muted-foreground">
                {step.bdd.location.file}:{step.bdd.location.line}
              </Text>
              {step.bdd.argument ? <BddArgumentView argument={step.bdd.argument} /> : null}
            </div>
          ) : null}
        </div>
        <Text size="xs" className="shrink-0 pt-0.5 tabular-nums text-muted-foreground">
          {step.durationMs != null ? formatDurationMs(step.durationMs) : '—'}
        </Text>
      </div>
      {failure ? <FailureBlock failure={failure} onWatch={onWatch} /> : null}
    </div>
  );
}

function StepFilterToggle({
  value,
  onChange,
}: {
  value: StepFilter;
  onChange: (value: StepFilter) => void;
}) {
  return (
    <Tabs
      selectedKey={value}
      onSelectionChange={(key) => onChange(key === 'failures' ? 'failures' : 'all')}
      className="w-auto"
    >
      <TabList aria-label="Step filter">
        <Tab id="all">All steps</Tab>
        <Tab id="failures">Failures only</Tab>
      </TabList>
    </Tabs>
  );
}

function BddContext({ item }: { item: ExternalRunItemDto }) {
  const bdd = item.bdd;
  if (!bdd) return null;
  const Source = ({ file, line }: { file: string; line: number }) => (
    <span className="font-mono text-muted-foreground">
      {file}:{line}
    </span>
  );
  return (
    <div className="space-y-2 px-2 py-1" aria-label="BDD context" data-testid="bdd-context">
      <div>
        <Text size="xs" weight="medium">
          Feature · {bdd.feature.name}
        </Text>
        <div className="text-xs text-muted-foreground">
          <Source {...bdd.feature.location} />
        </div>
        {bdd.feature.description ? (
          <p className="mt-1 whitespace-pre-wrap break-words text-xs text-muted-foreground">
            {bdd.feature.description}
          </p>
        ) : null}
      </div>
      {bdd.rule ? (
        <div>
          <Text size="xs" weight="medium">
            Rule · {bdd.rule.name}
          </Text>
          <div className="text-xs text-muted-foreground">
            <Source {...bdd.rule.location} />
          </div>
          {bdd.rule.description ? (
            <p className="mt-1 whitespace-pre-wrap break-words text-xs text-muted-foreground">
              {bdd.rule.description}
            </p>
          ) : null}
        </div>
      ) : null}
      <div>
        <Text size="xs" weight="medium">
          Scenario · {bdd.scenario.name}
        </Text>
        <div className="text-xs text-muted-foreground">
          <Source {...bdd.scenario.location} />
        </div>
        {bdd.scenario.description ? (
          <p className="mt-1 whitespace-pre-wrap break-words text-xs text-muted-foreground">
            {bdd.scenario.description}
          </p>
        ) : null}
      </div>
      {bdd.example ? (
        <div>
          <Text size="xs" weight="medium">
            Outline example · line {bdd.example.line}, column {bdd.example.column}
          </Text>
          <table className="mt-1 border-collapse text-xs">
            <tbody>
              {bdd.example.values.map(({ column, value }, index) => (
                <tr key={`${column}-${index}`}>
                  <th scope="row" className="border border-border px-2 py-1 text-left font-medium">
                    {column}
                  </th>
                  <td className="border border-border px-2 py-1 whitespace-pre-wrap">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function AttemptList({
  attempts,
  selectedItemId,
  isInProgress,
  recordingSource,
  onSelect,
}: {
  attempts: ExternalRunItemDto[];
  selectedItemId: string;
  isInProgress: boolean;
  recordingSource: string | null;
  onSelect: (itemId: string) => void;
}) {
  if (!attempts.length) return null;
  const label = (status: ExternalRunItemDto['status']) =>
    status === 'passed' ? 'Passed' : status === 'failed' ? 'Failed' : 'Skipped';
  return (
    <section
      className="space-y-1 px-2 py-1"
      aria-label="Recorded attempts"
      data-testid="attempt-list"
    >
      {recordingSource ? (
        <Text size="xs" className="text-muted-foreground">
          Scenario recording uses {recordingSource}.
        </Text>
      ) : null}
      <div className="space-y-1">
        {attempts.map((attempt) => {
          const attemptIsRunning = isInProgress && !attempt.finishedAt && !!attempt.startedAt;
          const elapsed = elapsedTestAttemptMs(
            attempt.startedAt,
            attempt.finishedAt ?? (attemptIsRunning ? new Date().toISOString() : null)
          );
          return (
            <div
              key={attempt.id}
              className={`rounded-sm px-2 py-1 ${attempt.id === selectedItemId ? 'bg-muted/50' : ''}`}
              data-testid="attempt-row"
            >
              <button
                type="button"
                aria-pressed={attempt.id === selectedItemId}
                aria-label={`Attempt ${attempt.attempt}, ${label(attempt.status)}, Total Time ${formatElapsedDurationMs(elapsed)}${elapsed === null && attempt.durationMs !== null ? `, Provider Duration ${formatDurationMs(attempt.durationMs)}` : ''}`}
                onClick={() => onSelect(attempt.id)}
                className="flex w-full items-center justify-between gap-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Text size="xs" weight="medium">
                    Attempt {attempt.attempt}
                  </Text>
                  <Text
                    size="xs"
                    className={
                      attempt.status === 'failed' ? 'text-destructive' : 'text-muted-foreground'
                    }
                  >
                    {label(attempt.status)}
                  </Text>
                </span>
                <Text size="xs" className="shrink-0 tabular-nums text-muted-foreground">
                  {formatElapsedDurationMs(elapsed)}
                </Text>
              </button>
              {elapsed === null && attempt.durationMs !== null ? (
                <Text size="xs" className="ml-auto block text-right text-muted-foreground">
                  Provider Duration · {formatDurationMs(attempt.durationMs)}
                </Text>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function AttemptDetailTabs({
  attempts,
  selectedItem,
  isInProgress,
  recordingSource,
  onSelectAttempt,
}: {
  attempts: ExternalRunItemDto[];
  selectedItem: ExternalRunItemDto;
  isInProgress: boolean;
  recordingSource: string | null;
  onSelectAttempt: (itemId: string) => void;
}) {
  return (
    <div
      className="px-3 pb-2 pt-2"
      data-testid="attempt-detail-tabs"
    >
      <Tabs defaultSelectedKey="attempts">
        <TabList aria-label="Selected attempt details">
          <Tab id="attempts">Attempts</Tab>
          <Tab id="output">Output</Tab>
          {selectedItem.bdd ? <Tab id="bdd">BDD Context</Tab> : null}
        </TabList>
        <TabPanel id="attempts">
          <AttemptList
            attempts={attempts}
            selectedItemId={selectedItem.id}
            isInProgress={isInProgress}
            recordingSource={recordingSource}
            onSelect={onSelectAttempt}
          />
        </TabPanel>
        <TabPanel id="output">
          {selectedItem.output ? (
            <ExternalTestOutputSection output={selectedItem.output} />
          ) : (
            <Text size="xs" className="px-2 py-3 text-muted-foreground">
              No output recorded for this attempt.
            </Text>
          )}
        </TabPanel>
        {selectedItem.bdd ? (
          <TabPanel id="bdd">
            <BddContext item={selectedItem} />
          </TabPanel>
        ) : null}
      </Tabs>
    </div>
  );
}

const STEPS_HEADING = 'text-xs font-medium uppercase tracking-wide text-muted-foreground';

/**
 * The centre column: what the test did, step by step.
 *
 * Playwright/BDD tests show their native steps grouped under Background and
 * Scenario, with the failing step expanded in place; other providers keep the
 * sectioned step list. Attempts / Output / BDD Context sit below.
 */
export function ExternalTestMain({
  scenario,
  run,
  selectedItem,
  attempts,
  recordingSource,
  onSelectAttempt,
  onSeek,
}: {
  scenario: Scenario;
  run: ExternalRunDetailDto;
  selectedItem: ExternalRunItemDto;
  attempts: ExternalRunItemDto[];
  recordingSource: string | null;
  onSelectAttempt: (itemId: string) => void;
  /** Seek the recording; omitted when this attempt has none. */
  onSeek?: (ms: number) => void;
}) {
  const [filter, setFilter] = React.useState<StepFilter>('all');
  const isInProgress = run.status === 'open' || run.status === 'collecting';
  const nativeSteps = run.provider === 'playwright' ? (selectedItem.nativeSteps ?? []) : null;
  // A 49-step test is unreadable fully expanded, so open only what the viewer
  // came for: the sections that actually broke. When nothing broke there is no
  // "interesting" section, so open everything rather than an all-collapsed wall.
  const anySectionFailed = scenario.sections.some((s) =>
    s.steps.some((step) => step.verdict === 'failed' || step.verdict === 'flaky')
  );

  // The error belongs to the first failed step; failures outside any step
  // (setup, teardown) get a standalone block so the message is never lost.
  const itemError =
    selectedItem.status === 'failed' && selectedItem.error?.message
      ? { message: selectedItem.error.message, stack: selectedItem.error.stack }
      : null;
  const firstFailedIndex = nativeSteps?.find((step) => step.status === 'failed')?.index ?? null;
  const videoOffsetMs = selectedItem.capture?.videoOffsetMs ?? null;
  const watchAt = (startMs: number | undefined) =>
    onSeek && selectedItem.capture ? () => onSeek((videoOffsetMs ?? 0) + (startMs ?? 0)) : undefined;

  const groups = nativeSteps ? groupNativeSteps(nativeSteps, filter) : [];

  return (
    <div className="space-y-4">
      <section className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2">
          <Text size="xs" weight="medium" className="uppercase tracking-wide text-muted-foreground">
            Steps
          </Text>
          {nativeSteps && nativeSteps.length > 0 ? (
            <StepFilterToggle value={filter} onChange={setFilter} />
          ) : null}
        </div>

        {nativeSteps !== null ? (
          nativeSteps.length > 0 ? (
            <div data-testid="native-step-list" aria-label="Playwright test steps">
              {itemError && firstFailedIndex === null ? (
                <div className="pt-3">
                  <FailureBlock failure={itemError} onWatch={watchAt(undefined)} />
                </div>
              ) : null}
              {groups.length === 0 ? (
                <Text size="xs" className="block px-4 py-3 text-muted-foreground">
                  No failed steps.
                </Text>
              ) : null}
              {groups.map((group) => (
                <div key={group.key}>
                  {group.label ? (
                    <div className={`border-b border-border bg-muted/30 px-4 py-1.5 ${STEPS_HEADING}`}>
                      {group.label} · {group.total} step{group.total === 1 ? '' : 's'}
                    </div>
                  ) : null}
                  {group.steps.map(({ step, number }) => (
                    <NativeStepRow
                      key={step.index}
                      step={step}
                      number={number}
                      failure={step.index === firstFailedIndex ? (itemError ?? undefined) : undefined}
                      onWatch={watchAt(step.startMs)}
                    />
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="px-4 py-3" data-testid="native-step-empty-state">
              <Text size="xs" className="text-muted-foreground">
                No provider steps recorded.
              </Text>
              {itemError ? (
                <div className="pt-3">
                  <FailureBlock failure={itemError} onWatch={watchAt(undefined)} />
                </div>
              ) : null}
            </div>
          )
        ) : (
          scenario.sections.map((section, si) => {
            const sectionFailed = section.steps.some(
              (step) => step.verdict === 'failed' || step.verdict === 'flaky'
            );
            const rows = section.steps.map((step) => (
              <StepRow key={step.id} step={step} onSeek={onSeek} />
            ));

            // A suite without SECTION headers has one unlabelled group; wrapping
            // that in a collapsible called nothing would be a toggle for the whole
            // step list, so render it plainly instead.
            if (!section.label) {
              return <div key={`steps-${si}`}>{rows}</div>;
            }

            return (
              <CollapsibleSection
                key={`${section.label}-${si}`}
                label={section.label}
                count={section.steps.length}
                defaultExpanded={anySectionFailed ? sectionFailed : true}
                triggerClassName="mx-0 px-3"
                bodyClassName="pb-1"
                hideBorder={si === 0}
              >
                {rows}
              </CollapsibleSection>
            );
          })
        )}
      </section>

      <section className="rounded-lg border border-border bg-card">
        <AttemptDetailTabs
          attempts={attempts}
          selectedItem={selectedItem}
          isInProgress={isInProgress}
          recordingSource={run.provider === 'playwright' ? null : recordingSource}
          onSelectAttempt={onSelectAttempt}
        />
      </section>
    </div>
  );
}
